import { createSelector } from '@reduxjs/toolkit';

import { equalsIgnoreCase } from 'src/utils/stringUtils';

import { touristSelector } from 'src/store/tourists/selectors';
import type { DataStateType } from '../data/types';

const getData = (state): DataStateType => state.data;

// citizenships
const _getCitizenships = createSelector(getData, (data) => data.citizenships);

export const getCitizenships = createSelector(
  _getCitizenships,
  (citizenships): { value: number; label: string }[] =>
    citizenships
      ? citizenships.map(([label, value]) => ({ value, label }))
      : [],
);

export const getCitizenshipsWithCode = createSelector(
  _getCitizenships,
  (citizenships) =>
    citizenships
      ? citizenships.reduce(
          (acc, item) => ({ ...acc, [item[1]]: item[2]['data-mask-name'] }),
          {},
        )
      : {},
);

export const getCitizenshipCode = createSelector(
  [_getCitizenships, touristSelector],
  (citizenships, tourist) => {
    if (tourist?.nationality_id) {
      const neededItem = citizenships.find(
        (item) => item[1] === tourist.nationality_id,
      );
      return neededItem?.[2]['data-mask-name'];
    }
    return '';
  },
);

// validatedTourists
const getValidatedTourists = createSelector(
  getData,
  (data) => data.validatedTourists,
);

export const getIsTouristValidated = createSelector(
  [getValidatedTourists, (_, index) => index],
  (validatedTourists, index) =>
    validatedTourists && validatedTourists.includes(index),
);

export const getIsTouristValidatedCount = createSelector(
  getValidatedTourists,
  (validatedTourists) => validatedTourists.length,
);

export const makeGetIsTouristValidated = () => getIsTouristValidated;

// mockTourists
export const getMockTourists = createSelector(
  getData,
  (data) => data.mockTourists,
);

// tourists
export const getSavedTourists = createSelector(
  getData,
  (data) => data.tourists,
);

export const getSavedTouristsByDocType = createSelector(
  [getSavedTourists, (_, docType) => docType],
  (savedTourists, docType) =>
    savedTourists?.filter((tourist) =>
      equalsIgnoreCase(docType, tourist.docType),
    ),
);

// resetDefaultClientData
const _getIsResetDefaultClientData = createSelector(
  getData,
  (data) => data.resetDefaultClientData,
);

export const getIsResetDefaultClientData = createSelector(
  _getIsResetDefaultClientData,
  (isResetDefaultClientData) => Boolean(isResetDefaultClientData),
);
